import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Card, 
  CardContent,
  Grid,
  Divider,
  Paper,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import html2pdf from 'html2pdf.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`termene-tabpanel-${index}`}
      aria-labelledby={`termene-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const CalculatorTermene = () => {
  const [tabValue, setTabValue] = useState(0);
  
  // State pentru calculator individual
  const [dataReferinta, setDataReferinta] = useState(null);
  const [tipTermen, setTipTermen] = useState('apel');
  const [modCalcul, setModCalcul] = useState('libere'); // 'libere' sau 'pline'
  const [rezultatTermen, setRezultatTermen] = useState(null);
  
  // State pentru calculator multiplu
  const [dataReferintaMultiplu, setDataReferintaMultiplu] = useState(null);
  const [termeneSelectate, setTermeneSelectate] = useState([]);
  const [modCalculMultiplu, setModCalculMultiplu] = useState('libere');
  const [rezultateMultiple, setRezultateMultiple] = useState([]);
  
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [zileLibereSuplimentare, setZileLibereSuplimentare] = useState([]);

  // Definirea termenelor legale
  const termene = {
    // Termene procesuale civile
    apel: { zile: 30, descriere: 'Apel', tip: 'Proces civil' },
    recurs: { zile: 15, descriere: 'Recurs', tip: 'Proces civil' },
    contestatie: { zile: 15, descriere: 'Contestație', tip: 'Proces civil' },
    opozitie: { zile: 15, descriere: 'Opoziție', tip: 'Proces civil' },
    revizuire: { zile: 30, descriere: 'Cerere de revizuire', tip: 'Proces civil' },
    
    // Termene procesuale penale
    apel_penal: { zile: 10, descriere: 'Apel penal', tip: 'Proces penal' },
    recurs_penal: { zile: 10, descriere: 'Recurs penal', tip: 'Proces penal' },
    
    // Termene administrative
    contestatie_administrativa: { zile: 30, descriere: 'Contestație act administrativ', tip: 'Contencios administrativ' },
    plangere_prealabila: { zile: 30, descriere: 'Plângere prealabilă', tip: 'Contencios administrativ' },
    raspuns_plangere: { zile: 45, descriere: 'Răspuns la plângere prealabilă', tip: 'Contencios administrativ' },
    
    // Termene comerciale
    contestatie_executare: { zile: 15, descriere: 'Contestație executare silită', tip: 'Proces comercial' },
    opozitie_ordonanta: { zile: 5, descriere: 'Opoziție la ordonanță președințială', tip: 'Proces comercial' },
    anulare_arbitraj: { zile: 90, descriere: 'Cerere anulare hotărâre arbitrală', tip: 'Arbitraj' },
    
    // Termene fiscale
    contestatie_fiscala: { zile: 30, descriere: 'Contestație fiscală', tip: 'Contencios fiscal' },
    plangere_fiscala: { zile: 45, descriere: 'Plângere împotriva deciziei', tip: 'Contencios fiscal' }
  };

  // Încarcă zilele libere suplimentare din JSON extern
  useEffect(() => {
    const incarcaZileLibereSuplimentare = async () => {
      try {
        const response = await fetch('https://juristpro.ro/calculator/zilelibere.json');
        if (response.ok) {
          const data = await response.json();
          setZileLibereSuplimentare(data.zileSuplimentare || []);
        }
      } catch (error) {
        console.log('Nu s-au putut încărca zilele libere suplimentare:', error);
        // Continuă cu zilele libere standard
      }
    };
    
    incarcaZileLibereSuplimentare();
  }, []);

  // Funcție pentru calcularea datei Paștelui Ortodox (algoritmul exact din calculator-termene.ro)
  const calculeazaPasteleOrtodox = (an) => {
    // Începem cu 4 aprilie
    let dataPaste = new Date(an, 3, 4); // luna 3 = aprilie (0-indexed)
    
    // Calculăm offset-ul conform algoritmului lor
    const a = ((an % 19) * 19 + 15) % 30;
    dataPaste.setDate(dataPaste.getDate() + a);
    
    const s = ((an % 4) * 2 + (an % 7) * 4 + 6 * a + 6) % 7;
    dataPaste.setDate(dataPaste.getDate() + s);
    
    return dataPaste;
  };

  // Funcție pentru obținerea tuturor sărbătorilor legale pentru un an (conform calculator-termene.ro)
  const getSarbatoriLegale = (an) => {
    const sarbatori = [];
    
    // Sărbători fixe (exact ca în calculator-termene.ro)
    sarbatori.push(new Date(an, 0, 1));  // 1 ianuarie - Anul Nou
    sarbatori.push(new Date(an, 0, 2));  // 2 ianuarie - A doua zi de Anul Nou
    
    // Bobotează și Sf. Ioan (doar din 2024)
    if (an >= 2024) {
      sarbatori.push(new Date(an, 0, 6));  // 6 ianuarie - Bobotează
      sarbatori.push(new Date(an, 0, 7));  // 7 ianuarie - Sfântul Ioan Botezătorul
    }
    
    sarbatori.push(new Date(an, 0, 24)); // 24 ianuarie - Ziua Unirii
    sarbatori.push(new Date(an, 4, 1));  // 1 mai - Ziua Muncii
    sarbatori.push(new Date(an, 5, 1));  // 1 iunie - Ziua Copilului
    sarbatori.push(new Date(an, 7, 15)); // 15 august - Adormirea Maicii Domnului
    sarbatori.push(new Date(an, 10, 30)); // 30 noiembrie - Sfântul Andrei
    sarbatori.push(new Date(an, 11, 1)); // 1 decembrie - Ziua Națională a României
    sarbatori.push(new Date(an, 11, 25)); // 25 decembrie - Ziua de Crăciun
    sarbatori.push(new Date(an, 11, 26)); // 26 decembrie - A doua zi de Crăciun
    
    // Paștele și sărbătorile mobile
    const pastele = calculeazaPasteleOrtodox(an);
    
    // Vinerea Mare (2 zile înainte de Paște) - doar din 2018
    if (an >= 2018) {
      const vinereaMare = new Date(pastele);
      vinereaMare.setDate(vinereaMare.getDate() - 2);
      sarbatori.push(vinereaMare);
    }
    
    // Paștele (Duminica)
    sarbatori.push(new Date(pastele));
    
    // Lunea Paștelui
    const luneaPastelui = new Date(pastele);
    luneaPastelui.setDate(luneaPastelui.getDate() + 1);
    sarbatori.push(luneaPastelui);
    
    // Rusaliile (49 zile după Paște - Duminica)
    const rusaliile = new Date(pastele);
    rusaliile.setDate(rusaliile.getDate() + 49);
    sarbatori.push(rusaliile);
    
    // Lunea Rusaliilor (50 de zile după Paște)
    const luneaRusaliilor = new Date(pastele);
    luneaRusaliilor.setDate(luneaRusaliilor.getDate() + 50);
    sarbatori.push(luneaRusaliilor);
    
    return sarbatori;
  };

  // Funcție pentru verificarea dacă o dată este sărbătoare legală
  const esteSarbatoareLegala = (data) => {
    const an = data.getFullYear();
    const sarbatori = getSarbatoriLegale(an);
    
    // Verifică sărbătorile standard
    const esteSarbatoareStandard = sarbatori.some(sarbatoare => 
      sarbatoare.getDate() === data.getDate() &&
      sarbatoare.getMonth() === data.getMonth() &&
      sarbatoare.getFullYear() === data.getFullYear()
    );
    
    if (esteSarbatoareStandard) return true;
    
    // Verifică zilele libere suplimentare din JSON
    const dataStr = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
    return zileLibereSuplimentare.some(zi => zi.data === dataStr);
  };

  // Funcție pentru verificarea dacă o dată este zi lucrătoare
  const esteZiLucratoare = (data) => {
    const ziSaptamana = data.getDay();
    // 0 = Duminică, 6 = Sâmbătă
    if (ziSaptamana === 0 || ziSaptamana === 6) {
      return false;
    }
    
    // Verifică dacă este sărbătoare legală
    if (esteSarbatoareLegala(data)) {
      return false;
    }
    
    return true;
  };

  // Funcție pentru ajustarea datei pentru weekend (exact ca funcția Y din calculator-termene.ro)
  const ajusteazaPentruWeekend = (data) => {
    const ziSaptamana = data.getDay();
    // Dacă e sâmbătă (6) sau duminică (0), mută la luni
    if (ziSaptamana === 6) {
      data.setDate(data.getDate() + 2); // Sâmbătă -> Luni
    } else if (ziSaptamana === 0) {
      data.setDate(data.getDate() + 1); // Duminică -> Luni
    }
  };

  // Funcție recursivă pentru ajustarea datei (exact ca funcția W din calculator-termene.ro)
  const ajusteazaDataFinala = (data) => {
    // Ajustează pentru weekend
    ajusteazaPentruWeekend(data);
    
    // Verifică dacă este sărbătoare
    if (esteSarbatoareLegala(data)) {
      // Dacă e sărbătoare, adaugă o zi și verifică din nou
      data.setDate(data.getDate() + 1);
      ajusteazaDataFinala(data); // Recursiv
    }
  };

  // Funcție pentru calcularea zilelor lucrătoare (exact ca funcția Z din calculator-termene.ro)
  const calculeazaZileLucratoare = (dataStart, numarZile, zilePline = false) => {
    // Clonăm data de start și resetăm ora
    let dataCalculata = new Date(dataStart);
    dataCalculata.setHours(0, 0, 0, 0);
    
    let zileDeAdaugat = numarZile;
    
    if (zilePline) {
      // Mod "Zile pline": scade 1 zi (ziua de start se numără)
      zileDeAdaugat -= 1;
    } else {
      // Mod "Zile libere": adaugă 1 zi la început (ziua de start NU se numără)
      dataCalculata.setDate(dataCalculata.getDate() + 1);
    }
    
    // Adăugăm numărul de zile CALENDARISTICE
    dataCalculata.setDate(dataCalculata.getDate() + zileDeAdaugat);
    
    // Ajustăm pentru weekend-uri și sărbători
    ajusteazaDataFinala(dataCalculata);
    
    return dataCalculata;
  };

  // Funcție pentru numărarea zilelor libere prinse în interval
  const numaraZileLiberePrinse = (dataStart, dataEnd) => {
    let zileLiberePrinse = [];
    let dataTemp = new Date(dataStart);
    dataTemp.setDate(dataTemp.getDate() + 1); // Începem de a doua zi
    
    while (dataTemp <= dataEnd) {
      const ziSaptamana = dataTemp.getDay();
      const esteWeekend = ziSaptamana === 0 || ziSaptamana === 6;
      const esteSarbatoare = esteSarbatoareLegala(dataTemp);
      
      if (esteWeekend || esteSarbatoare) {
        const numeZi = esteSarbatoare ? getNumeSarbatoare(dataTemp) : (ziSaptamana === 0 ? 'Duminică' : 'Sâmbătă');
        zileLiberePrinse.push({
          data: new Date(dataTemp),
          nume: numeZi,
          tip: esteSarbatoare ? 'sarbatoare' : 'weekend'
        });
      }
      
      dataTemp.setDate(dataTemp.getDate() + 1);
    }
    
    return zileLiberePrinse;
  };

  // Funcție pentru obținerea numelui sărbătorii
  const getNumeSarbatoare = (data) => {
    const zi = data.getDate();
    const luna = data.getMonth();
    const an = data.getFullYear();
    
    // Sărbători fixe
    if (luna === 0 && zi === 1) return 'Anul Nou';
    if (luna === 0 && zi === 2) return 'A doua zi de Anul Nou';
    if (luna === 0 && zi === 6) return 'Bobotează';
    if (luna === 0 && zi === 7) return 'Sfântul Ioan Botezătorul';
    if (luna === 0 && zi === 24) return 'Ziua Unirii';
    if (luna === 4 && zi === 1) return 'Ziua Muncii';
    if (luna === 5 && zi === 1) return 'Ziua Copilului';
    if (luna === 7 && zi === 15) return 'Adormirea Maicii Domnului';
    if (luna === 10 && zi === 30) return 'Sfântul Andrei';
    if (luna === 11 && zi === 1) return 'Ziua Națională a României';
    if (luna === 11 && zi === 25) return 'Ziua de Crăciun';
    if (luna === 11 && zi === 26) return 'A doua zi de Crăciun';
    
    // Sărbători mobile
    const pastele = calculeazaPasteleOrtodox(an);
    
    // Vinerea Mare
    if (an >= 2018) {
      const vinereaMare = new Date(pastele);
      vinereaMare.setDate(vinereaMare.getDate() - 2);
      if (data.getTime() === vinereaMare.getTime()) return 'Vinerea Mare';
    }
    
    // Paștele
    if (data.getTime() === pastele.getTime()) return 'Ziua de Paște';
    
    // Lunea Paștelui
    const luneaPastelui = new Date(pastele);
    luneaPastelui.setDate(luneaPastelui.getDate() + 1);
    if (data.getTime() === luneaPastelui.getTime()) return 'A doua zi de Paște';
    
    // Rusaliile
    const rusaliile = new Date(pastele);
    rusaliile.setDate(rusaliile.getDate() + 49);
    if (data.getTime() === rusaliile.getTime()) return 'Rusalii';
    
    // Lunea Rusaliilor
    const luneaRusaliilor = new Date(pastele);
    luneaRusaliilor.setDate(luneaRusaliilor.getDate() + 50);
    if (data.getTime() === luneaRusaliilor.getTime()) return 'A doua zi de Rusalii';
    
    return 'Sărbătoare legală';
  };

  // Calculează termenul individual
  useEffect(() => {
    if (dataReferinta && tipTermen) {
      const termenInfo = termene[tipTermen];
      const zilePline = modCalcul === 'pline';
      const dataLimita = calculeazaZileLucratoare(dataReferinta, termenInfo.zile, zilePline);
      
      // Calculează zilele libere prinse
      const zileLiberePrinse = numaraZileLiberePrinse(dataReferinta, dataLimita);
      
      // Calculează zilele rămase
      const azi = new Date();
      const diferentaMs = dataLimita.getTime() - azi.getTime();
      const zileRamase = Math.ceil(diferentaMs / (1000 * 60 * 60 * 24));
      
      let statusTermen = 'normal';
      if (zileRamase < 0) {
        statusTermen = 'expirat';
      } else if (zileRamase <= 3) {
        statusTermen = 'urgent';
      } else if (zileRamase <= 7) {
        statusTermen = 'atentie';
      }
      
      setRezultatTermen({
        tipTermen: termenInfo.descriere,
        tip: termenInfo.tip,
        numarZile: termenInfo.zile,
        dataReferinta: dataReferinta.toLocaleDateString('ro-RO'),
        dataLimita: dataLimita.toLocaleDateString('ro-RO'),
        zileRamase: zileRamase,
        statusTermen: statusTermen,
        zileLiberePrinse: zileLiberePrinse
      });
    } else {
      setRezultatTermen(null);
    }
  }, [dataReferinta, tipTermen, modCalcul]);

  // Calculează termene multiple
  useEffect(() => {
    if (dataReferintaMultiplu && termeneSelectate.length > 0) {
      const zilePline = modCalculMultiplu === 'pline';
      const rezultate = termeneSelectate.map(tipTermen => {
        const termenInfo = termene[tipTermen];
        const dataLimita = calculeazaZileLucratoare(dataReferintaMultiplu, termenInfo.zile, zilePline);
        
        // Calculează zilele libere prinse
        const zileLiberePrinse = numaraZileLiberePrinse(dataReferintaMultiplu, dataLimita);
        
        const azi = new Date();
        const diferentaMs = dataLimita.getTime() - azi.getTime();
        const zileRamase = Math.ceil(diferentaMs / (1000 * 60 * 60 * 24));
        
        let statusTermen = 'normal';
        if (zileRamase < 0) {
          statusTermen = 'expirat';
        } else if (zileRamase <= 3) {
          statusTermen = 'urgent';
        } else if (zileRamase <= 7) {
          statusTermen = 'atentie';
        }
        
        return {
          tipTermen: termenInfo.descriere,
          tip: termenInfo.tip,
          numarZile: termenInfo.zile,
          dataLimita: dataLimita,
          dataLimitaFormatata: dataLimita.toLocaleDateString('ro-RO'),
          zileRamase: zileRamase,
          statusTermen: statusTermen,
          zileLiberePrinse: zileLiberePrinse
        };
      });
      
      // Sortează după data limită
      rezultate.sort((a, b) => a.dataLimita - b.dataLimita);
      setRezultateMultiple(rezultate);
    } else {
      setRezultateMultiple([]);
    }
  }, [dataReferintaMultiplu, termeneSelectate, modCalculMultiplu]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const resetCalculatorIndividual = () => {
    setDataReferinta(null);
    setTipTermen('apel');
    setModCalcul('libere');
    setRezultatTermen(null);
  };

  const resetCalculatorMultiplu = () => {
    setDataReferintaMultiplu(null);
    setTermeneSelectate([]);
    setModCalculMultiplu('libere');
    setRezultateMultiple([]);
  };

  const copiazaRezultat = (rezultat) => {
    if (rezultat) {
      const textDeCopiat = `${rezultat.tipTermen}: Data limită ${rezultat.dataLimita} (${rezultat.zileRamase > 0 ? `${rezultat.zileRamase} zile rămase` : 'EXPIRAT'})`;
      navigator.clipboard.writeText(textDeCopiat).then(() => {
        setOpenSnackbar(true);
      });
    }
  };

  const exportaPDF = (rezultate, tip) => {
    if ((tip === 'individual' && rezultatTermen) || (tip === 'multiplu' && rezultate.length > 0)) {
      const element = document.createElement('div');
      const dataExport = tip === 'individual' ? [rezultatTermen] : rezultate;
      
      element.innerHTML = `
        <div style="padding: 20px; font-family: Arial, sans-serif;">
          <h2 style="text-align: center; color: #333; border-bottom: 2px solid #ccc; padding-bottom: 10px;">
            Calculator Termene Procesuale
          </h2>
          
          <div style="margin: 20px 0;">
            ${dataExport.map(rezultat => `
              <div style="margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 5px;">
                <h3 style="color: #333; margin-bottom: 10px;">${rezultat.tipTermen}</h3>
                
                <div style="display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #eee;">
                  <span><strong>Tip:</strong></span>
                  <span>${rezultat.tip}</span>
                </div>
                
                <div style="display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #eee;">
                  <span><strong>Data de referință:</strong></span>
                  <span>${tip === 'individual' ? rezultat.dataReferinta : dataReferintaMultiplu.toLocaleDateString('ro-RO')}</span>
                </div>
                
                <div style="display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #eee;">
                  <span><strong>Termen legal:</strong></span>
                  <span>${rezultat.numarZile} zile</span>
                </div>
                
                <div style="display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #eee;">
                  <span><strong>Data limită:</strong></span>
                  <span style="font-weight: bold; color: ${rezultat.statusTermen === 'expirat' ? '#dc3545' : rezultat.statusTermen === 'urgent' ? '#fd7e14' : '#28a745'};">
                    ${tip === 'individual' ? rezultat.dataLimita : rezultat.dataLimitaFormatata}
                  </span>
                </div>
                
                <div style="display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #eee;">
                  <span><strong>Status:</strong></span>
                  <span style="font-weight: bold; color: ${rezultat.statusTermen === 'expirat' ? '#dc3545' : rezultat.statusTermen === 'urgent' ? '#fd7e14' : '#28a745'};">
                    ${rezultat.zileRamase > 0 ? `${rezultat.zileRamase} zile rămase` : 'EXPIRAT'}
                  </span>
                </div>
              </div>
            `).join('')}
          </div>
          
          <div style="margin-top: 30px; padding: 15px; background-color: #fff3cd; border: 1px solid #ffeaa7; border-radius: 5px;">
            <p style="margin: 0; font-size: 12px; color: #856404;">
              <strong>Notă:</strong> Calculul exclude zilele de weekend și sărbătorile legale. 
              Verificați întotdeauna termenele cu legislația în vigoare și particularitățile fiecărui caz.
            </p>
          </div>
        </div>
      `;

      const opt = {
        margin: 1,
        filename: `termene-${tip}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      html2pdf().set(opt).from(element).save();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'expirat': return '#dc3545';
      case 'urgent': return '#fd7e14';
      case 'atentie': return '#ffc107';
      default: return '#28a745';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'expirat': return <WarningIcon sx={{ color: '#dc3545' }} />;
      case 'urgent': return <WarningIcon sx={{ color: '#fd7e14' }} />;
      case 'atentie': return <ScheduleIcon sx={{ color: '#ffc107' }} />;
      default: return <CheckCircleIcon sx={{ color: '#28a745' }} />;
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        <ScheduleIcon style={{ marginRight: '8px', verticalAlign: 'middle' }} />
        Calculator Termene Procesuale
      </Typography>
      
      <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#f0f7ff', borderRadius: 1, border: '1px solid #d0e7ff' }}>
        <Typography variant="body2" color="primary.main">
          Aplicația permite calculul termenelor judiciare implementând metodele de calcul prevăzute de Codul civil și de Codul de procedură civilă.
        </Typography>
      </Box>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="termene tabs"
          sx={{
            '& .MuiTab-root': {
              minWidth: 150,
              padding: '14px 28px',
              fontSize: '0.9rem',
              fontWeight: 500,
              textTransform: 'none',
              borderRadius: '25px 25px 0 0',
              margin: '0 2px',
              transition: 'all 0.3s ease-in-out',
              border: '2px solid #e0e0e0',
              borderBottom: 'none',
              backgroundColor: '#fafafa',
              color: '#757575',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#424242',
                color: '#212121',
                transform: 'translateY(-2px)',
              },
              '&.Mui-selected': {
                backgroundColor: '#424242',
                borderColor: '#424242',
                color: 'white',
                fontWeight: 600,
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(66, 66, 66, 0.3)',
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            }
          }}
        >
          <Tab label="Termen Individual" />
          <Tab label="Termene Multiple" />
          <Tab label="Zile Libere Naționale" />
        </Tabs>
      </Box>

      {/* Tab pentru Termen Individual */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
              <Typography variant="body2" color="success.main">
                <strong>Calculator Individual:</strong> Calculează un singur termen procesual cu excluderea zilelor nelucrătoare și sărbătorilor legale.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Data de referință <span style={{ color: 'red' }}>*</span>
                </Typography>
                <DatePicker
                  selected={dataReferinta}
                  onChange={(date) => setDataReferinta(date)}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="zi.luna.an"
                  customInput={
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  }
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Mod calcul
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={modCalcul === 'pline'}
                        onChange={(e) => setModCalcul(e.target.checked ? 'pline' : 'libere')}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {modCalcul === 'libere' ? 'Zile libere' : 'Zile pline'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {modCalcul === 'libere' 
                            ? 'Ziua de start NU se numără' 
                            : 'Ziua de start SE numără'}
                        </Typography>
                      </Box>
                    }
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Tip termen <span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={tipTermen}
                    onChange={(e) => setTipTermen(e.target.value)}
                  >
                    <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Procese Civile
                    </MenuItem>
                    <MenuItem value="apel">Apel (30 zile)</MenuItem>
                    <MenuItem value="recurs">Recurs (15 zile)</MenuItem>
                    <MenuItem value="contestatie">Contestație (15 zile)</MenuItem>
                    <MenuItem value="opozitie">Opoziție (15 zile)</MenuItem>
                    <MenuItem value="revizuire">Revizuire (30 zile)</MenuItem>
                    
                    <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Procese Penale
                    </MenuItem>
                    <MenuItem value="apel_penal">Apel penal (10 zile)</MenuItem>
                    <MenuItem value="recurs_penal">Recurs penal (10 zile)</MenuItem>
                    
                    <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Contencios Administrativ
                    </MenuItem>
                    <MenuItem value="contestatie_administrativa">Contestație act adm. (30 zile)</MenuItem>
                    <MenuItem value="plangere_prealabila">Plângere prealabilă (30 zile)</MenuItem>
                    <MenuItem value="raspuns_plangere">Răspuns plângere (45 zile)</MenuItem>
                    
                    <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Procese Comerciale
                    </MenuItem>
                    <MenuItem value="contestatie_executare">Contestație exec. (15 zile)</MenuItem>
                    <MenuItem value="opozitie_ordonanta">Opoziție ordonanță (5 zile)</MenuItem>
                    <MenuItem value="anulare_arbitraj">Anulare arbitraj (90 zile)</MenuItem>
                    
                    <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Contencios Fiscal
                    </MenuItem>
                    <MenuItem value="contestatie_fiscala">Contestație fiscală (30 zile)</MenuItem>
                    <MenuItem value="plangere_fiscala">Plângere fiscală (45 zile)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={resetCalculatorIndividual}
                  sx={{ marginRight: 2 }}
                >
                  Reset
                </Button>
                
                {rezultatTermen && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={() => exportaPDF(null, 'individual')}
                      sx={{ marginRight: 2 }}
                    >
                      Export PDF
                    </Button>
                    
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => copiazaRezultat(rezultatTermen)}
                    >
                      Copiază rezultatul
                    </Button>
                  </>
                )}
              </Grid>
            </Grid>

            {rezultatTermen && (
              <Paper 
                elevation={3} 
                sx={{ 
                  marginTop: 3, 
                  padding: 3, 
                  backgroundColor: '#f8f9fa',
                  border: `2px solid ${getStatusColor(rezultatTermen.statusTermen)}`,
                  borderRadius: 2
                }}
              >
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    textAlign: 'center', 
                    fontWeight: 'bold',
                    color: getStatusColor(rezultatTermen.statusTermen),
                    marginBottom: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1
                  }}
                >
                  {getStatusIcon(rezultatTermen.statusTermen)}
                  {rezultatTermen.tipTermen}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Tip proces:
                      </Typography>
                      <Chip label={rezultatTermen.tip} color="primary" size="small" />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Termen legal:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {rezultatTermen.numarZile} zile lucrătoare
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ marginY: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Data de referință:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {rezultatTermen.dataReferinta}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                        Data limită:
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: getStatusColor(rezultatTermen.statusTermen) }}>
                        {rezultatTermen.dataLimita}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                        Status:
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: getStatusColor(rezultatTermen.statusTermen) }}>
                        {rezultatTermen.zileRamase > 0 ? `${rezultatTermen.zileRamase} zile rămase` : 'EXPIRAT'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                {rezultatTermen.zileLiberePrinse && rezultatTermen.zileLiberePrinse.length > 0 && (
                  <Box sx={{ marginTop: 2 }}>
                    <Divider sx={{ marginY: 2 }} />
                    <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1, color: '#1976d2' }}>
                      Zile libere prinse în interval ({rezultatTermen.zileLiberePrinse.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {rezultatTermen.zileLiberePrinse.map((zi, index) => (
                        <Chip
                          key={index}
                          label={`${zi.data.toLocaleDateString('ro-RO')} - ${zi.nume}`}
                          size="small"
                          color={zi.tip === 'sarbatoare' ? 'error' : 'default'}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                
                <Box sx={{ marginTop: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1, color: '#1976d2' }}>
                    Notă importantă:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      backgroundColor: '#fff3e0',
                      padding: 2,
                      borderRadius: 1,
                      border: '1px solid #ffe0b2',
                      color: '#e65100'
                    }}
                  >
                    Calculul exclude zilele de weekend și sărbătorile legale. Verificați întotdeauna termenele cu legislația în vigoare și particularitățile fiecărui caz.
                  </Typography>
                </Box>
              </Paper>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab pentru Termene Multiple */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
              <Typography variant="body2" color="warning.main">
                <strong>Calculator Multiplu:</strong> Calculează mai multe termene simultan pentru o dată de referință comună. Util pentru gestionarea mai multor dosare.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Data de referință <span style={{ color: 'red' }}>*</span>
                </Typography>
                <DatePicker
                  selected={dataReferintaMultiplu}
                  onChange={(date) => setDataReferintaMultiplu(date)}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="zi.luna.an"
                  customInput={
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  }
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Mod calcul
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={modCalculMultiplu === 'pline'}
                        onChange={(e) => setModCalculMultiplu(e.target.checked ? 'pline' : 'libere')}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {modCalculMultiplu === 'libere' ? 'Zile libere' : 'Zile pline'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {modCalculMultiplu === 'libere' 
                            ? 'Ziua de start NU se numără' 
                            : 'Ziua de start SE numără'}
                        </Typography>
                      </Box>
                    }
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Termene selectate <span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    value={termeneSelectate}
                    onChange={(e) => setTermeneSelectate(e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={termene[value].descriere} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Procese Civile
                    </MenuItem>
                    <MenuItem value="apel">Apel (30 zile)</MenuItem>
                    <MenuItem value="recurs">Recurs (15 zile)</MenuItem>
                    <MenuItem value="contestatie">Contestație (15 zile)</MenuItem>
                    <MenuItem value="opozitie">Opoziție (15 zile)</MenuItem>
                    <MenuItem value="revizuire">Cerere de revizuire (30 zile)</MenuItem>
                    
                    <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Procese Penale
                    </MenuItem>
                    <MenuItem value="apel_penal">Apel penal (10 zile)</MenuItem>
                    <MenuItem value="recurs_penal">Recurs penal (10 zile)</MenuItem>
                    
                    <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Contencios Administrativ
                    </MenuItem>
                    <MenuItem value="contestatie_administrativa">Contestație act administrativ (30 zile)</MenuItem>
                    <MenuItem value="plangere_prealabila">Plângere prealabilă (30 zile)</MenuItem>
                    <MenuItem value="raspuns_plangere">Răspuns la plângere (45 zile)</MenuItem>
                    
                    <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Procese Comerciale
                    </MenuItem>
                    <MenuItem value="contestatie_executare">Contestație executare (15 zile)</MenuItem>
                    <MenuItem value="opozitie_ordonanta">Opoziție ordonanță (5 zile)</MenuItem>
                    <MenuItem value="anulare_arbitraj">Anulare arbitraj (90 zile)</MenuItem>
                    
                    <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Contencios Fiscal
                    </MenuItem>
                    <MenuItem value="contestatie_fiscala">Contestație fiscală (30 zile)</MenuItem>
                    <MenuItem value="plangere_fiscala">Plângere fiscală (45 zile)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={resetCalculatorMultiplu}
                  sx={{ marginRight: 2 }}
                >
                  Reset
                </Button>
                
                {rezultateMultiple.length > 0 && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={() => exportaPDF(rezultateMultiple, 'multiplu')}
                      sx={{ marginRight: 2 }}
                    >
                      Export PDF
                    </Button>
                  </>
                )}
              </Grid>
            </Grid>

            {rezultateMultiple.length > 0 && (
              <Paper 
                elevation={3} 
                sx={{ 
                  marginTop: 3, 
                  padding: 3, 
                  backgroundColor: '#f8f9fa',
                  border: '2px solid #e9ecef',
                  borderRadius: 2
                }}
              >
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    textAlign: 'center', 
                    fontWeight: 'bold',
                    color: '#495057',
                    marginBottom: 3
                  }}
                >
                  <CalendarTodayIcon sx={{ marginRight: 1 }} />
                  Termene Calculate ({rezultateMultiple.length})
                </Typography>
                
                <List>
                  {rezultateMultiple.map((rezultat, index) => (
                    <ListItem 
                      key={index}
                      sx={{ 
                        border: `1px solid ${getStatusColor(rezultat.statusTermen)}`,
                        borderRadius: 1,
                        marginBottom: 1,
                        backgroundColor: 'white'
                      }}
                    >
                      <ListItemIcon>
                        {getStatusIcon(rezultat.statusTermen)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              {rezultat.tipTermen}
                            </Typography>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: getStatusColor(rezultat.statusTermen) 
                              }}
                            >
                              {rezultat.dataLimitaFormatata}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              {rezultat.tip} • {rezultat.numarZile} zile
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: getStatusColor(rezultat.statusTermen),
                                marginTop: 0.5
                              }}
                            >
                              {rezultat.zileRamase > 0 ? `${rezultat.zileRamase} zile rămase` : 'EXPIRAT'}
                            </Typography>
                          </Box>
                        }
                      />
                      <Button
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => copiazaRezultat(rezultat)}
                      >
                        Copiază
                      </Button>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </CardContent>
        </Card>
      </TabPanel>
      
      {/* Tab pentru Zile Libere Naționale */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', marginBottom: 2 }}>
              Zile libere naționale pentru anul {new Date().getFullYear()}
            </Typography>
            
            <List>
              {getSarbatoriLegale(new Date().getFullYear()).map((sarbatoare, index) => {
                const numeSarbatoare = getNumeSarbatoare(sarbatoare);
                return (
                  <ListItem 
                    key={index}
                    sx={{ 
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      marginBottom: 1,
                      backgroundColor: 'white'
                    }}
                  >
                    <ListItemIcon>
                      <CalendarTodayIcon sx={{ color: '#1976d2' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {sarbatoare.toLocaleDateString('ro-RO', { day: 'numeric', month: 'long' })}
                        </Typography>
                      }
                      secondary={numeSarbatoare}
                    />
                  </ListItem>
                );
              })}
            </List>
            
            <Box sx={{ marginTop: 3, padding: 2, backgroundColor: '#f0f7ff', borderRadius: 1 }}>
              <Typography variant="body2" color="primary.main">
                Aceste zile sunt excluse automat din calculul termenelor judiciare, împreună cu zilele de weekend (sâmbătă și duminică).
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>
      
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success">
          Rezultatul a fost copiat în clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CalculatorTermene;